import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Clock, Zap, X, CreditCard } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "hg_renewal_dismiss_until";

export default function RenewalBanner() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dismissedUntil, setDismissedUntil] = useState(() => {
        const v = localStorage.getItem(DISMISS_KEY);
        return v ? parseInt(v, 10) : 0;
    });

    useEffect(() => {
        let alive = true;
        api.get("/payments/renewal-status")
            .then(r => { if (alive) setStatus(r.data); })
            .catch(() => { if (alive) setStatus(null); });
        return () => { alive = false; };
    }, []);

    const visible = useMemo(() => {
        if (!status?.should_remind) return false;
        if (Date.now() < dismissedUntil) return false;
        return true;
    }, [status, dismissedUntil]);

    const dismiss = () => {
        // Snooze for 12 hours
        const next = Date.now() + 12 * 60 * 60 * 1000;
        localStorage.setItem(DISMISS_KEY, String(next));
        setDismissedUntil(next);
    };

    const handleRenew = async () => {
        setLoading(true);
        try {
            // Auto-renewing sub: open Stripe portal to manage / cancel.
            if (status?.auto_renews) {
                const { data } = await api.post("/payments/portal", {
                    origin_url: window.location.origin,
                });
                if (data?.url) {
                    window.location.href = data.url;
                } else {
                    toast.error("Could not open billing portal");
                }
                return;
            }
            // Canceling sub / legacy one-time: take them to checkout to renew.
            const { data } = await api.post("/payments/renew", {
                origin_url: window.location.origin,
                plan: status?.plan || "monthly",
            });
            if (data?.url) {
                window.location.href = data.url;
            } else {
                toast.error("Could not start renewal");
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || "Renewal failed. Please try again.";
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    };

    if (!visible) return null;

    const days = Math.max(0, status.days_remaining ?? 0);
    const dayLabel = days === 0
        ? (status.hours_remaining && status.hours_remaining > 0 ? `in ${status.hours_remaining} hours` : "today")
        : days === 1 ? "in 1 day" : `in ${days} days`;
    const planLabel = status.plan_label || "Premium";

    const stage = status.stage;
    const isAutoRenew = stage === "7d_auto" || status.reason === "auto_renew_heads_up";
    const isUrgent = !isAutoRenew && (stage === "3d" || stage === "1d");

    // Eyebrow + tone + access wording differ for auto-renew (soft, no urgency)
    // vs. canceling / legacy one-time (escalating ladder).
    let eyebrow;
    let tone;
    let accessVerb;
    if (isAutoRenew) {
        eyebrow = "Renewing soon";
        tone = "No action needed if you want to keep training. We'll just charge your card on file.";
        accessVerb = "auto-renews";
    } else if (stage === "30d") {
        eyebrow = "Heads up · access ending";
        tone = "Plenty of time — but knowing now beats getting surprised then.";
        accessVerb = "ends";
    } else if (stage === "7d") {
        eyebrow = "Access ends in one week";
        tone = "Lock it in this week so you don't lose a session to a paywall.";
        accessVerb = "ends";
    } else if (stage === "1d") {
        eyebrow = "Final day";
        tone = "Last call before access is paused.";
        accessVerb = "ends";
    } else if (stage === "3d") {
        eyebrow = "Access ends soon";
        tone = "Don't break the chain.";
        accessVerb = "ends";
    } else {
        eyebrow = status.reason === "sub_canceling" ? "Subscription ending" : "Renewal due";
        tone = "Don't break the chain.";
        accessVerb = "ends";
    }

    // Visual: auto-renew = calm slate, urgent = red gradient, soft = neutral.
    const containerCls = isAutoRenew
        ? "relative w-full border-b border-white/15 bg-gradient-to-r from-[#0f1216] via-[#0a0d10] to-[#0f1216] text-white"
        : isUrgent
            ? "relative w-full border-b-2 border-accent-red bg-gradient-to-r from-[#1a0606] via-[#0f0303] to-[#1a0606] text-white"
            : "relative w-full border-b border-accent-red/50 bg-gradient-to-r from-[#101418] via-[#0a0d10] to-[#101418] text-white";
    const iconCls = isAutoRenew ? "text-white/65" : (isUrgent ? "text-accent-red" : "text-accent-red/70");

    return (
        <div
            data-testid="renewal-banner"
            data-stage={stage || "legacy"}
            data-auto-renew={isAutoRenew ? "1" : "0"}
            className={containerCls}
        >
            <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6">
                <div className={`hidden flex-none rounded-sm border ${isAutoRenew ? "border-white/15 bg-white/5" : "border-accent-red/40 bg-accent-red/15"} p-2 sm:block`}>
                    <Clock className={`h-5 w-5 ${iconCls}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className={`label-eyebrow ${isAutoRenew ? "text-white/75" : "text-accent-red"}`}>{eyebrow}</span>
                        <span className="text-xs text-white/60">{planLabel} plan</span>
                    </div>
                    <p className="mt-0.5 text-sm font-medium leading-snug sm:text-base">
                        Your {planLabel} plan {accessVerb} <span className={`font-bold ${isAutoRenew ? "text-white" : "text-accent-red"}`} data-testid="renewal-days">{dayLabel}</span>.
                        <span className="ml-1 text-white/75">{tone}</span>
                    </p>
                </div>
                <div className="flex flex-none items-center gap-2">
                    <button
                        onClick={handleRenew}
                        disabled={loading}
                        data-testid="renewal-banner-renew"
                        className={`flex items-center gap-1.5 rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-60 ${
                            isAutoRenew
                                ? "border border-white/20 bg-transparent text-white hover:bg-white/10"
                                : "bg-accent-red text-white shadow-[0_0_0_0_rgba(239,68,68,0)] hover:bg-[color:var(--accent-hover)] hover:shadow-[0_0_24px_-4px_rgba(239,68,68,0.7)]"
                        }`}
                    >
                        {loading ? (
                            <>
                                <Zap className="h-3.5 w-3.5 animate-pulse" /> Loading…
                            </>
                        ) : isAutoRenew ? (
                            <>
                                <CreditCard className="h-3.5 w-3.5" /> Manage subscription
                            </>
                        ) : (
                            <>
                                <CreditCard className="h-3.5 w-3.5" /> Renew now
                            </>
                        )}
                    </button>
                    <button
                        onClick={dismiss}
                        aria-label="Dismiss for 12 hours"
                        data-testid="renewal-banner-dismiss"
                        className="rounded-sm border border-white/15 p-2 text-white/70 hover:bg-white/5 hover:text-white"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
